/**
 * Money as integer minor units.
 *
 * INVARIANT (AGENTS.md §3.1): money never touches `number`. `amount` is a
 * `bigint` count of the currency's smallest unit — 4732n is €47.32, 4732n is
 * also ¥4732 because JPY has no minor unit. The currency is therefore not
 * decoration: it decides how the integer is read.
 *
 * Conversion to a human-readable decimal string happens exactly once, at the
 * UI boundary, via `formatMoney`. If you are dividing by 100 anywhere else,
 * something has gone wrong.
 */
import { AllocationError, CurrencyMismatchError, InvalidAmountError, InvalidCurrencyError } from './errors.js';
/**
 * Currencies whose minor unit is not 1/100. Anything absent defaults to 2
 * decimals, which is correct for the overwhelming majority of ISO 4217.
 */
const CURRENCY_DECIMALS = {
    BHD: 3,
    BIF: 0,
    CLF: 4,
    CLP: 0,
    DJF: 0,
    GNF: 0,
    IQD: 3,
    ISK: 0,
    JOD: 3,
    JPY: 0,
    KMF: 0,
    KRW: 0,
    KWD: 3,
    LYD: 3,
    OMR: 3,
    PYG: 0,
    RWF: 0,
    TND: 3,
    UGX: 0,
    UYW: 4,
    VND: 0,
    VUV: 0,
    XAF: 0,
    XOF: 0,
    XPF: 0,
};
const DEFAULT_DECIMALS = 2;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;
export function assertValidCurrency(currency) {
    if (!CURRENCY_PATTERN.test(currency))
        throw new InvalidCurrencyError(currency);
}
/** Number of decimal places the currency's minor unit represents. */
export function decimalsFor(currency) {
    assertValidCurrency(currency);
    return CURRENCY_DECIMALS[currency] ?? DEFAULT_DECIMALS;
}
export function money(amount, currency) {
    assertValidCurrency(currency);
    return { amount, currency };
}
export function zero(currency) {
    return money(0n, currency);
}
function assertSameCurrency(a, b) {
    if (a.currency !== b.currency)
        throw new CurrencyMismatchError(a.currency, b.currency);
}
export function add(a, b) {
    assertSameCurrency(a, b);
    return { amount: a.amount + b.amount, currency: a.currency };
}
export function subtract(a, b) {
    assertSameCurrency(a, b);
    return { amount: a.amount - b.amount, currency: a.currency };
}
export function negate(a) {
    return { amount: -a.amount, currency: a.currency };
}
export function isZero(a) {
    return a.amount === 0n;
}
export function sum(amounts, currency) {
    assertValidCurrency(currency);
    let total = 0n;
    for (const m of amounts) {
        if (m.currency !== currency)
            throw new CurrencyMismatchError(currency, m.currency);
        total += m.amount;
    }
    return { amount: total, currency };
}
export function compare(a, b) {
    assertSameCurrency(a, b);
    if (a.amount < b.amount)
        return -1;
    if (a.amount > b.amount)
        return 1;
    return 0;
}
/**
 * Integer division rounding half away from zero — the rule people expect when
 * they see a price rounded, and symmetric for negative amounts so a refund
 * rounds the mirror image of the purchase.
 */
export function divideRound(numerator, denominator) {
    if (denominator === 0n)
        throw new InvalidAmountError('Division by zero');
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const quotient = n / d;
    const remainder = n % d;
    const rounded = remainder * 2n >= d ? quotient + 1n : quotient;
    return negative ? -rounded : rounded;
}
/**
 * Split `total` into parts proportional to `weights`, using the largest
 * remainder method so the parts sum to exactly `total` — no lost or invented
 * cents. This is what makes "50/30/20 of €2,333.33" add back up.
 *
 * Ties in the remainder are broken by earlier index, so the result is
 * deterministic for a given input order.
 */
export function allocate(total, weights) {
    if (weights.length === 0)
        throw new AllocationError('Cannot allocate across zero weights');
    if (weights.some((w) => !Number.isFinite(w) || w < 0))
        throw new AllocationError('Weights must be finite and non-negative');
    const weightTotal = weights.reduce((acc, w) => acc + w, 0);
    if (weightTotal <= 0)
        throw new AllocationError('Weights must sum to a positive number');
    // Scale weights to integers so the arithmetic stays exact in bigint space.
    const SCALE = 1_000_000;
    const scaled = weights.map((w) => BigInt(Math.round(w * SCALE)));
    const scaledTotal = scaled.reduce((acc, w) => acc + w, 0n);
    if (scaledTotal === 0n)
        throw new AllocationError('Weights must sum to a positive number');
    const negative = total.amount < 0n;
    const magnitude = negative ? -total.amount : total.amount;
    const floors = [];
    const remainders = [];
    let distributed = 0n;
    for (const weight of scaled) {
        const exact = magnitude * weight;
        const floor = exact / scaledTotal;
        floors.push(floor);
        remainders.push(exact % scaledTotal);
        distributed += floor;
    }
    let leftover = magnitude - distributed;
    const order = remainders
        .map((remainder, index) => ({ remainder, index }))
        .sort((a, b) => (a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1));
    for (const { index } of order) {
        if (leftover <= 0n)
            break;
        floors[index] = (floors[index] ?? 0n) + 1n;
        leftover -= 1n;
    }
    return floors.map((part) => ({
        amount: negative ? -part : part,
        currency: total.currency,
    }));
}
/** Split `total` into `parts` as evenly as integer minor units allow. */
export function allocateEvenly(total, parts) {
    if (!Number.isInteger(parts) || parts <= 0)
        throw new AllocationError('Part count must be a positive integer');
    return allocate(total, new Array(parts).fill(1));
}
/**
 * Format for display. The ONLY place minor units become a decimal string.
 * Grouping and symbol placement are the caller's locale concern; this returns
 * a plain, locale-neutral numeric string such as "-47.32" or "4732" (JPY).
 */
export function formatMoney(value) {
    const decimals = decimalsFor(value.currency);
    const negative = value.amount < 0n;
    const digits = (negative ? -value.amount : value.amount).toString().padStart(decimals + 1, '0');
    const whole = digits.slice(0, digits.length - decimals);
    const fraction = decimals > 0 ? `.${digits.slice(digits.length - decimals)}` : '';
    return `${negative ? '-' : ''}${whole}${fraction}`;
}
/**
 * Parse a decimal string a human typed ("47.32", "-1,5" is rejected) into
 * minor units. Rejects more decimal places than the currency has, rather than
 * silently rounding away money the user explicitly typed.
 */
export function parseMoney(input, currency) {
    const decimals = decimalsFor(currency);
    const trimmed = input.trim();
    const match = /^(-)?(\d+)(?:\.(\d+))?$/.exec(trimmed);
    if (!match)
        throw new InvalidAmountError(`"${input}" is not a valid decimal amount`);
    const [, sign, whole = '0', fraction = ''] = match;
    if (fraction.length > decimals)
        throw new InvalidAmountError(`${currency} has ${decimals} decimal place(s); "${input}" has ${fraction.length}`);
    const padded = fraction.padEnd(decimals, '0');
    const amount = BigInt(whole + padded);
    return { amount: sign === '-' ? -amount : amount, currency };
}
//# sourceMappingURL=money.js.map